import React, { FC, useState } from "react";
import styled from "styled-components";
import { Theme } from "@literati/public-ui-shared-react";
import LegacyThemeProvider from "components/Layout/DefaultThemeProvider";
import * as Yup from "yup";
import { Subscription } from "lib/api/subscription";
import StepLayout, {
  StepLayoutClassName,
  StepLayoutProps,
} from "components/Step/StepLayout";
import {
  ThemePreferenceKey as Key,
  ThemePreferenceSubmit,
  ThemePreferenceValues,
} from "components/Step/stepTypes";
import {
  getContentfulQuizStep,
  gsuSubmitAnswersDefault,
} from "components/Step/stepUtil";
import GuidedSignUpHeading from "components/GuidedSignUp/GuidedSignUpHeading";
import GuidedSignUpPillCloud from "components/GuidedSignUp/GuidedSignUpPillCloud";
import { guidedSignUpReplaceTemplateStrings } from "utils/guidedSignUpTemplating";

/**
 * Custom validator for theme preference.
 *
 * @note - We always submit all possible answer options with values set to `0` or `1`.
 *         This validator makes sure the initial state where all `value`s are `undefined`
 *         to prevent the user from proceeding without selecting at least 1 option.
 */
Yup.addMethod(Yup.mixed, "themePreference", function (errorMessage) {
  return this.test("test-theme-preference", errorMessage, function (value) {
    const { path, createError } = this;
    const hasUserValue = Boolean(
      value?.filter((option: Record<"value", any>) => option.value === 1).length
    );
    return hasUserValue || createError({ path, message: errorMessage });
  });
});

export const VALIDATION_SCHEMA = Yup.object().shape({
  [Key.THEME_PREFERENCES]: (Yup.mixed() as any).themePreference(
    "Please choose at least one theme or topic."
  ),
});

export const INITIAL_VALUES: ThemePreferenceValues = {
  [Key.KIND]: "reader-theme-preferences",
  [Key.THEME_PREFERENCES]: [],
};

export function getThemePreferencesStep(contentfulQuizModel?: any) {
  const step = getContentfulQuizStep(
    contentfulQuizModel,
    "reader-theme-preference"
  );
  return step;
}

const StyledStepLayout = styled(StepLayout)`
  .${StepLayoutClassName.FORM} {
    max-width: 42rem;
  }
`;

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 0;

  .dls-error-message {
    align-self: stretch;
    display: flex;
    justify-content: center;
    text-align: center;
    margin: 0.75rem 0 0;
  }
`;

const StyledGuidedSignUpPillCloud = styled(GuidedSignUpPillCloud)`
  margin: 0.75rem 0 0;
  position: sticky;
  left: 0;
`;

export type StepLayoutPropsThemePreferences = Omit<
  StepLayoutProps<ThemePreferenceValues>,
  "asForm" | "children" | "formId"
>;

export interface StepThemePreferencesProps
  extends StepLayoutPropsThemePreferences {
  childName?: string;
  contentfulQuizModelParsed?: any;
  initialValues?: Partial<ThemePreferenceValues> | null;
  onSubmit: ThemePreferenceSubmit;
  subscription?: Subscription;
}

export const StepThemePreferences = ({
  childName,
  contentfulQuizModelParsed,
  initialValues: initialValuesProp,
  onSubmit,
  subscription,
  ...stepLayoutProps
}: StepThemePreferencesProps) => {
  const [initialValues] = useState<ThemePreferenceValues>({
    ...INITIAL_VALUES,
    ...initialValuesProp,
  });

  const [step] = useState(getThemePreferencesStep(contentfulQuizModelParsed));

  const handleSubmit: ThemePreferenceSubmit = async (values, formikHelpers) => {
    await gsuSubmitAnswersDefault({
      contentfulQuizModel: contentfulQuizModelParsed,
      formikHelpers,
      onError: (res) => res, // @TODO
      onSubmit,
      step,
      subscription,
      values,
    });
    // if submission fails, ensure re-validation
    formikHelpers?.validateForm();
  };

  const primaryHeading = guidedSignUpReplaceTemplateStrings(step?.prompt, {
    childName,
  });

  const question = step?.questions?.[0];

  return (
    <Theme>
      <StyledStepLayout<FC<StepLayoutProps<ThemePreferenceValues>>>
        asForm
        bgColor="white"
        formId="activation-step-theme-preference"
        formikProps={{
          initialValues,
          onSubmit: handleSubmit,
          validationSchema: VALIDATION_SCHEMA,
        }}
        {...stepLayoutProps}
      >
        {({ dirty, errors, submitCount }) => {
          const showError = (Boolean(submitCount) || dirty) && Boolean(errors);

          return (
            <LegacyThemeProvider>
              <StyledDiv>
                <GuidedSignUpHeading
                  primaryHeading={primaryHeading}
                  subheading={step?.subtext}
                />
                <StyledGuidedSignUpPillCloud
                  name={question?.slug}
                  options={question?.answers}
                  showError={showError}
                />
              </StyledDiv>
            </LegacyThemeProvider>
          );
        }}
      </StyledStepLayout>
    </Theme>
  );
};

export default StepThemePreferences;
